import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * Back in stock signup form.
 *
 * Rendering is Liquid-driven (blocks/back-in-stock.liquid): the form only
 * exists in the DOM when the selected variant is out of stock. This component
 * follows the same pattern as product-inventory.js — it listens for the
 * product select event dispatched by the variant picker and morphs itself
 * from the section HTML that request already fetched, so show/hide and the
 * variant id always match the server's view of the selected variant.
 *
 * Submissions POST to the Cloudflare Worker configured on the block.
 */
class BackInStockComponent extends Component {
  /** @type {number} Timestamp used as a bot time-gate: reject submits made too quickly. */
  #shownAt = Date.now();

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event by morphing the block from the fetched
   * section HTML (which reflects the newly selected variant's availability).
   * @param {CustomEvent & { promise: Promise<{ detail: { html: Document, productId: string, newProduct?: { id: string } } }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newComponent = html.querySelector('back-in-stock-component');
        if (!newComponent || !(newComponent instanceof HTMLElement)) return;

        // Preserve anything the customer already typed across the morph.
        const typedEmail = this.refs.emailInput?.value ?? '';
        const consentChecked = this.refs.consentInput?.checked ?? false;
        const hadForm = Boolean(this.refs.form);

        morph(this, newComponent, { childrenOnly: true });

        // childrenOnly leaves host attributes alone — sync the out-of-stock
        // flag manually (the stylesheet keys off it to hide the personaliser).
        if (newComponent.dataset.oos) this.dataset.oos = newComponent.dataset.oos;

        const { emailInput, consentInput } = this.refs;
        if (emailInput && typedEmail) emailInput.value = typedEmail;
        if (consentInput && consentChecked) consentInput.checked = true;

        // Form just appeared (variant switched to out of stock): restart the time-gate.
        if (!hadForm && this.refs.form) this.#shownAt = Date.now();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[back-in-stock] Event promise rejected:', error);
      });
  };

  /**
   * Clears the consent error state once the checkbox is ticked.
   */
  clearConsentError = () => {
    const { consentInput, consentWrapper } = this.refs;
    if (consentInput?.checked) {
      consentWrapper?.classList.remove('back-in-stock__consent--invalid');
    }
  };

  /**
   * Handles the signup form submission.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { emailInput, consentInput, consentWrapper, honeypot, submitButton, message, variantInput } = this.refs;
    if (!emailInput || !consentInput || !submitButton || !message || !variantInput) return;

    if (submitButton.disabled) return;
    if (honeypot?.value) return;
    if (Date.now() - this.#shownAt < 1500) return;

    consentWrapper?.classList.remove('back-in-stock__consent--invalid');

    if (!emailInput.value || !emailInput.checkValidity()) {
      emailInput.reportValidity?.();
      emailInput.focus();
      return;
    }

    if (!consentInput.checked) {
      consentWrapper?.classList.add('back-in-stock__consent--invalid');
      return;
    }

    submitButton.disabled = true;
    message.textContent = this.dataset.msgSubmitting ?? '';

    try {
      const response = await fetch(this.dataset.workerUrl ?? '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailInput.value,
          variant_id: `gid://shopify/ProductVariant/${variantInput.value}`,
          product_id: `gid://shopify/Product/${this.dataset.productId}`,
          consent: consentInput.checked,
          locale: this.dataset.locale ?? '',
          honeypot: honeypot?.value ?? '',
        }),
      });

      const data = await response.json();

      if (data.ok) {
        message.textContent = this.dataset.msgSuccess ?? '';
        this.refs.form?.reset();
      } else if (response.status === 429) {
        message.textContent = this.dataset.msgRateLimit ?? '';
        submitButton.disabled = false;
      } else {
        message.textContent = this.dataset.msgError ?? '';
        submitButton.disabled = false;
      }
    } catch {
      message.textContent = this.dataset.msgError ?? '';
      submitButton.disabled = false;
    }
  };
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
